import {
  ClipboardCheck,
  Lock,
  MailOpen,
  Network,
  RefreshCw,
  ScrollText,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserX,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  ApiKeySummary,
  OrgMemberRoleAssignment,
  OrgMemberSummary,
  OrgRoleView,
  OrgUnitSummary,
} from '@nadoutong/contracts';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlatformApiError } from '../api/client.ts';
import { localizeApiError } from '../api/errorMessages.ts';
import { useDisplayRate } from '../api/useBilling.ts';
import { useKeysList } from '../api/useKeys.ts';
import {
  useAssignOrgMemberRole,
  useOrgInvitations,
  useOrgMembers,
  useOrgUnits,
  useUpdateOrgMember,
} from '../api/useOrgs.ts';
import { useOrgRoles } from '../api/useRoles.ts';
import { formatUsdCentsAsCny } from '../components/formatters.ts';
import { PanelError } from '../components/PanelError.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { isValidDisplayRate } from '../components/spendLimit.ts';
import { useToast } from '../components/Toast.tsx';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Button, Chip, ConfirmDialog, TBody, Table, Td, Th, THead, Tr } from '../ui/index.ts';
import { AuditPanel } from './organization/AuditPanel.tsx';
import { InvitationsPanel } from './organization/InvitationsPanel.tsx';
import { MemberKeysDialog } from './organization/MemberKeysDialog.tsx';
import { MemberLimitDialog } from './organization/MemberLimitDialog.tsx';
import { PermissionsPanel } from './organization/PermissionsPanel.tsx';
import { RolesPanel } from './organization/RolesPanel.tsx';
import { StructurePanel } from './organization/StructurePanel.tsx';
import { BUILTIN_ORG_ROLES_FALLBACK } from './organization/builtinOrgRoles.ts';
import {
  assignableOrgRoles,
  builtinRoleLabelKey,
  isBuiltinOrgRoleId,
  memberAssignedRoleId,
  roleHasMembersManage,
} from './organization/orgRoleLabels.ts';
import { formatDate } from './organization/formatDate.ts';
import {
  modelPermissionModeLabelKey,
  modelPermissionModeTone,
} from './organization/modelPermissionMode.ts';

const TAB_IDS = [
  'members',
  'structure',
  'permissions',
  'model_permissions',
  'invitations',
  'governance',
  'audit',
] as const;
type TabId = (typeof TAB_IDS)[number];
type UnitFilter = 'all' | 'unassigned' | string;

const DEFAULT_TAB: TabId = 'members';

const TAB_LABEL_KEY: Readonly<Record<TabId, I18nKey>> = {
  members: 'organization.tab.members',
  structure: 'organization.tab.structure',
  permissions: 'organization.tab.permissions',
  model_permissions: 'organization.tab.model_permissions',
  invitations: 'organization.tab.invitations',
  governance: 'organization.tab.governance',
  audit: 'organization.tab.audit',
};

const ROLE_LABEL_KEY: Readonly<Record<OrgMemberSummary['role'], I18nKey>> = {
  owner: 'organization.role.owner',
  admin: 'organization.role.admin',
  member: 'organization.role.member',
  finance: 'organization.role.finance',
};

export function Organization(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const orgId = user?.orgId ?? undefined;
  const canReadMembers = hasPermission(user, 'org:members:read');
  const canManageMembers = hasPermission(user, 'org:members:manage');
  const canManageInvitations = hasPermission(user, 'org:invitations:manage');
  // The key list is a separate resource with its own permission: a
  // manager who may read the directory is not automatically allowed to
  // see which credentials belong to whom. Gating the query (not just the
  // cell) means no `/v1/keys` request is issued on their behalf.
  const canReadOrgKeys = hasPermission(user, 'org:api_keys:read');
  // Revoking a member's key is a separate, stronger grant than seeing
  // it: the dialog lists with `read` and only offers revoke with
  // `manage` — the same split console-web's member dialog draws.
  const canManageOrgKeys = hasPermission(user, 'org:api_keys:manage');
  // Audit is offered as a tab only to accounts allowed to read the log;
  // the panel is what fires the query, so hiding the tab also means no
  // request is ever made on their behalf.
  const canReadAudit =
    hasPermission(user, 'org:audit:read') || hasPermission(user, 'member:audit:read');
  const membersQ = useOrgMembers(canReadMembers ? orgId : undefined);
  const unitsQ = useOrgUnits(canReadMembers ? orgId : undefined);
  const invitationsQ = useOrgInvitations(canReadMembers ? orgId : undefined);
  const rolesQ = useOrgRoles(canReadMembers ? orgId : undefined);
  const keysQ = useKeysList(canReadMembers && canReadOrgKeys);
  const updateMember = useUpdateOrgMember();
  const updateMemberStatus = useUpdateOrgMember();
  const assignRole = useAssignOrgMemberRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState<UnitFilter>('all');
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [keysMember, setKeysMember] = useState<OrgMemberSummary | null>(null);
  const [limitMember, setLimitMember] = useState<OrgMemberSummary | null>(null);
  // Keyed by account so the message stays attached to the row that
  // failed; a bare string would follow whichever select was touched last.
  const [assignError, setAssignError] = useState<{
    readonly accountId: string;
    readonly message: string;
  } | null>(null);
  const [roleError, setRoleError] = useState<{
    readonly accountId: string;
    readonly message: string;
  } | null>(null);
  const [statusError, setStatusError] = useState<{
    readonly accountId: string;
    readonly message: string;
  } | null>(null);
  const [disableTarget, setDisableTarget] = useState<OrgMemberSummary | null>(null);

  const members = membersQ.data?.members ?? [];
  const units = unitsQ.data?.units ?? [];
  const invitations = invitationsQ.data?.invitations ?? [];
  const rolesDegraded = rolesQ.isError;
  const roles = rolesQ.data?.roles ?? BUILTIN_ORG_ROLES_FALLBACK;
  // A revoked key is history, not an assignment — counting it would tell
  // a manager a member still holds a credential that no longer works.
  const activeKeys = useMemo(
    () => (keysQ.data?.keys ?? []).filter((key) => key.revokedAt === null),
    [keysQ.data],
  );
  const memberKeyCounts = useMemo(
    () => countMemberApiKeys(activeKeys, members),
    [activeKeys, members],
  );
  const defaultUnitId = useMemo(() => pickDefaultUnitId(units), [units]);
  const visibleTabs = TAB_IDS.filter((tab) => tab !== 'audit' || canReadAudit);

  // Tab selection lives in the URL, not component state: `/organization?tab=audit`
  // is then shareable, restorable and part of browser history — the same
  // treatment console-web gives its routed team pages. An unknown or
  // not-permitted `tab` falls back to the default instead of rendering blank.
  const requestedTab = searchParams.get('tab');
  const activeTab: TabId =
    isTabId(requestedTab) && visibleTabs.includes(requestedTab) ? requestedTab : DEFAULT_TAB;

  function selectTab(tab: TabId): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === DEFAULT_TAB) {
          next.delete('tab');
        } else {
          next.set('tab', tab);
        }
        if (tab !== 'audit') {
          // Audit filters belong to the audit view; carrying them onto
          // another tab would put unreadable state in a shared link.
          next.delete('action');
          next.delete('target');
        }
        return next;
      },
      { replace: false },
    );
  }

  useEffect(() => {
    if (units.length === 0) {
      setSelectedUnitId(null);
      return;
    }
    if (selectedUnitId === null || !units.some((unit) => unit.id === selectedUnitId)) {
      setSelectedUnitId(defaultUnitId);
    }
  }, [defaultUnitId, selectedUnitId, units]);

  const selectedUnit = units.find((unit) => unit.id === selectedUnitId) ?? null;
  const filteredMembers = useMemo(
    () => filterMembers(members, search, unitFilter),
    [members, search, unitFilter],
  );
  const assignedMembers = members.filter((member) => member.orgUnitId !== null).length;
  const limitedMembers = members.filter((member) => member.monthlyLimitUsdCents !== null).length;
  const managerMembers = members.filter(
    (member) => member.role === 'owner' || member.role === 'admin',
  ).length;
  const anyLoading = membersQ.isLoading || unitsQ.isLoading || invitationsQ.isLoading;
  const statusPendingAccountId = updateMemberStatus.isPending
    ? updateMemberStatus.variables?.accountId
    : undefined;
  const dialogErrorAccountId =
    disableTarget !== null && statusError?.accountId === disableTarget.accountId
      ? disableTarget.accountId
      : null;

  function refreshAll(): void {
    void membersQ.refetch();
    void unitsQ.refetch();
    void invitationsQ.refetch();
    void rolesQ.refetch();
    // Refetching a disabled query would still hit the network, so the
    // permission check has to be repeated here.
    if (canReadMembers && canReadOrgKeys) {
      void keysQ.refetch();
    }
  }

  /**
   * The `<select>` is controlled by server state, so a rejected PATCH
   * reverts it on the next render — the picked department simply flicks
   * back to the old one with nothing on screen explaining why, which
   * reads as the click having missed. Hence the explicit report, and
   * next to the offending row rather than in a toast: the list can be
   * long and a toast would not say which member failed.
   */
  function handleAssignment(member: OrgMemberSummary, nextOrgUnitId: string): void {
    if (!canManageMembers || orgId === undefined) {
      return;
    }
    setAssignError(null);
    updateMember.mutate(
      {
        orgId,
        accountId: member.accountId,
        body: {
          primaryOrgUnitId: nextOrgUnitId.length === 0 ? null : nextOrgUnitId,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('organization.members.toast.unit_updated'));
        },
        onError: (err) => {
          setAssignError({
            accountId: member.accountId,
            message: formatOrgApiError(err, t('organization.members.error.unit_update'), t),
          });
        },
      },
    );
  }

  function handleRoleAssignment(member: OrgMemberSummary, nextRole: string): void {
    if (!canManageMembers || orgId === undefined || member.role === 'owner') {
      return;
    }
    setRoleError(null);
    const body: OrgMemberRoleAssignment =
      isBuiltinOrgRoleId(nextRole) && nextRole !== 'owner'
        ? { role: nextRole }
        : { roleId: nextRole };
    assignRole.mutate(
      { orgId, accountId: member.accountId, body },
      {
        onSuccess: () => {
          toast.success(t('organization.members.toast.role_updated'));
        },
        onError: (err) => {
          setRoleError({
            accountId: member.accountId,
            message: formatOrgApiError(err, t('organization.members.error.role_update'), t),
          });
        },
      },
    );
  }

  function memberStatusError(member: OrgMemberSummary, err: unknown, enabling: boolean): string {
    const label = memberLabel(member);
    const fallback = t(
      enabling ? 'organization.members.error.enable' : 'organization.members.error.disable',
      { member: label },
    );
    if (!(err instanceof PlatformApiError)) {
      return fallback;
    }
    return t('organization.members.error.status_cause', {
      member: label,
      detail: formatOrgApiError(err, fallback, t),
    });
  }

  function handleDisableRequest(member: OrgMemberSummary): void {
    if (orgId === undefined || !canToggleMemberStatus(member, user?.accountId, canManageMembers)) {
      return;
    }
    setStatusError(null);
    setDisableTarget(member);
  }

  function handleConfirmDisable(): void {
    if (
      disableTarget === null ||
      orgId === undefined ||
      !canToggleMemberStatus(disableTarget, user?.accountId, canManageMembers)
    ) {
      return;
    }
    const member = disableTarget;
    setStatusError(null);
    updateMemberStatus.mutate(
      { orgId, accountId: member.accountId, body: { status: 'disabled' } },
      {
        onSuccess: () => {
          setDisableTarget(null);
          toast.success(t('organization.members.toast.disabled'));
        },
        onError: (err) => {
          setStatusError({
            accountId: member.accountId,
            message: memberStatusError(member, err, false),
          });
        },
      },
    );
  }

  function handleEnableMember(member: OrgMemberSummary): void {
    if (
      orgId === undefined ||
      member.status !== 'disabled' ||
      !canToggleMemberStatus(member, user?.accountId, canManageMembers)
    ) {
      return;
    }
    setStatusError(null);
    updateMemberStatus.mutate(
      { orgId, accountId: member.accountId, body: { status: 'active' } },
      {
        onSuccess: () => {
          toast.success(t('organization.members.toast.enabled'));
        },
        onError: (err) => {
          setStatusError({
            accountId: member.accountId,
            message: memberStatusError(member, err, true),
          });
        },
      },
    );
  }

  if (!canReadMembers) {
    return (
      <section data-testid="organization-page" className="flex flex-col gap-4">
        <h2 className="font-display text-display text-ink">{t('organization.title')}</h2>
        <p className="rounded-lg border border-line bg-card p-4 text-body text-ink-low">
          {t('organization.forbidden')}
        </p>
      </section>
    );
  }

  return (
    <section data-testid="organization-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex max-w-3xl flex-col gap-2">
          <h2 className="font-display text-display text-ink">{t('organization.title')}</h2>
          <p className="text-body leading-6 text-ink-mid">{t('organization.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={refreshAll}
          title={t('organization.refresh')}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-line bg-card px-4 py-2 text-body font-medium text-ink transition-colors hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        >
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          <span>{t('organization.refresh')}</span>
        </button>
      </header>

      <div
        data-testid="organization-tabs"
        className="flex flex-wrap gap-2 rounded-lg border border-line bg-raised p-1"
      >
        {visibleTabs.map((tab) => (
          <button
            type="button"
            key={tab}
            data-testid={`organization-tab-${tab}`}
            aria-current={activeTab === tab ? 'page' : undefined}
            onClick={() => selectTab(tab)}
            className={[
              'inline-flex items-center gap-2 rounded-md px-3 py-2 text-body font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
              activeTab === tab
                ? 'bg-active text-forest'
                : 'text-ink-low hover:bg-card hover:text-ink',
            ].join(' ')}
          >
            <TabIconGlyph tab={tab} />
            <span>{t(TAB_LABEL_KEY[tab])}</span>
          </button>
        ))}
      </div>

      <SummaryRail
        memberCount={members.length}
        unitCount={unitsQ.data?.summary.totalUnits ?? units.length}
        unassignedCount={unitsQ.data?.summary.unassignedMembers ?? 0}
        pendingCount={invitations.length}
      />

      {anyLoading ? (
        <LoadingState />
      ) : (
        <>
          {membersQ.isError ? (
            <QueryError
              error={membersQ.error}
              fallback={t('organization.error.members')}
              testId="organization-members-error"
              onRetry={() => {
                void membersQ.refetch();
              }}
            />
          ) : null}
          {unitsQ.isError ? (
            <QueryError
              error={unitsQ.error}
              fallback={t('organization.error.units')}
              testId="organization-units-error"
              onRetry={() => {
                void unitsQ.refetch();
              }}
            />
          ) : null}
          {invitationsQ.isError ? (
            <QueryError
              error={invitationsQ.error}
              fallback={t('organization.error.invitations')}
              testId="organization-invitations-error"
              onRetry={() => {
                void invitationsQ.refetch();
              }}
            />
          ) : null}
          {/* `canReadOrgKeys` is redundant but deliberate: the query is
              already disabled without the permission, so it cannot be in
              an error state. Keep the guard — the error surface must not
              depend on how the query happens to be gated today. */}
          {canReadOrgKeys && keysQ.isError ? (
            <QueryError
              error={keysQ.error}
              fallback={t('organization.error.keys')}
              testId="organization-keys-error"
              onRetry={() => {
                void keysQ.refetch();
              }}
            />
          ) : null}
          {activeTab === 'members' ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
              <div className="flex min-w-0 flex-col gap-4">
                <DirectoryFilters
                  units={units}
                  search={search}
                  unitFilter={unitFilter}
                  onSearch={setSearch}
                  onUnitFilter={setUnitFilter}
                />
                <MemberDirectory
                  members={filteredMembers}
                  units={units}
                  roles={roles}
                  currentAccountId={user?.accountId}
                  canManageMembers={canManageMembers}
                  canReadOrgKeys={canReadOrgKeys}
                  keyCounts={memberKeyCounts}
                  keysLoading={keysQ.isLoading}
                  assignmentPending={updateMember.isPending}
                  rolePending={assignRole.isPending}
                  statusPending={updateMemberStatus.isPending}
                  statusPendingAccountId={statusPendingAccountId}
                  assignError={assignError}
                  roleError={roleError}
                  statusError={statusError}
                  dialogErrorAccountId={dialogErrorAccountId}
                  onAssignment={handleAssignment}
                  onRoleAssignment={handleRoleAssignment}
                  onDisable={handleDisableRequest}
                  onEnable={handleEnableMember}
                  onSelectUnit={setSelectedUnitId}
                  onManageKeys={setKeysMember}
                  onEditLimit={setLimitMember}
                />
              </div>
              <OrgUnitDetail unit={selectedUnit} units={units} />
            </div>
          ) : null}
          {activeTab === 'structure' ? (
            <StructurePanel
              orgId={orgId}
              units={units}
              canManageUnits={canManageMembers}
              onSelectUnit={setSelectedUnitId}
            />
          ) : null}
          {activeTab === 'permissions' ? (
            <RolesPanel
              orgId={orgId}
              roles={roles}
              degraded={rolesDegraded}
              canManage={canManageMembers}
            />
          ) : null}
          {activeTab === 'model_permissions' ? (
            <PermissionsPanel
              orgId={orgId}
              members={members}
              units={units}
              canManage={canManageMembers}
            />
          ) : null}
          {activeTab === 'invitations' ? (
            <InvitationsPanel
              orgId={orgId}
              invitations={invitations}
              canManageInvitations={canManageInvitations}
            />
          ) : null}
          {activeTab === 'governance' ? (
            <GovernancePanel
              assignedMembers={assignedMembers}
              limitedMembers={limitedMembers}
              managerMembers={managerMembers}
            />
          ) : null}
          {activeTab === 'audit' && canReadAudit ? <AuditPanel orgId={orgId} /> : null}
          {activeTab === 'members' ? (
            <InvitationsPanel
              orgId={orgId}
              invitations={invitations}
              canManageInvitations={canManageInvitations}
              compact
            />
          ) : null}
        </>
      )}
      {/* `canReadOrgKeys` is redundant but deliberate: the button that
          sets `keysMember` only renders with the permission. It stays
          because this dialog shows key material — a second lock on the
          door is worth more than one line of dead code is worth saving. */}
      {keysMember !== null && canReadOrgKeys ? (
        <MemberKeysDialog
          member={keysMember}
          keys={keysForMember(activeKeys, keysMember)}
          canRevoke={canManageOrgKeys}
          onClose={() => setKeysMember(null)}
        />
      ) : null}
      {limitMember !== null && canManageMembers && orgId !== undefined ? (
        <MemberLimitDialog
          orgId={orgId}
          member={limitMember}
          onClose={() => setLimitMember(null)}
        />
      ) : null}
      <ConfirmDialog
        open={disableTarget !== null}
        onOpenChange={(open) => {
          if (!open && !updateMemberStatus.isPending) {
            setDisableTarget(null);
          }
        }}
        tone="danger"
        title={t('organization.members.status.disable_title')}
        body={
          <div className="flex flex-col gap-2">
            <p className="m-0">
              {t('organization.members.status.disable_body', {
                member: disableTarget === null ? '' : memberLabel(disableTarget),
              })}
            </p>
            {statusError !== null && dialogErrorAccountId === statusError.accountId ? (
              <p
                role="alert"
                data-testid={`organization-member-status-error-${statusError.accountId}`}
                className="m-0 text-caption text-bad"
              >
                {statusError.message}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={t('organization.members.status.disable_confirm')}
        cancelLabel={t('organization.action.cancel')}
        closeLabel={t('organization.members.status.disable_close')}
        loading={updateMemberStatus.isPending}
        onConfirm={handleConfirmDisable}
        testId="organization-member-disable-confirm"
      />
    </section>
  );
}

function SummaryRail({
  memberCount,
  unitCount,
  unassignedCount,
  pendingCount,
}: {
  readonly memberCount: number;
  readonly unitCount: number;
  readonly unassignedCount: number;
  readonly pendingCount: number;
}): JSX.Element {
  const t = useT();
  const items = [
    { id: 'members', label: t('organization.summary.members'), value: memberCount, icon: Users },
    { id: 'units', label: t('organization.summary.units'), value: unitCount, icon: Network },
    {
      id: 'unassigned',
      label: t('organization.summary.unassigned'),
      value: unassignedCount,
      icon: UserX,
    },
    {
      id: 'pending',
      label: t('organization.summary.pending'),
      value: pendingCount,
      icon: MailOpen,
    },
  ];
  return (
    <div
      data-testid="organization-summary"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center gap-3 rounded-lg border border-line bg-card px-4 py-3"
        >
          <item.icon aria-hidden="true" className="h-5 w-5 shrink-0 text-forest" />
          <div className="flex min-w-0 flex-col">
            <span className="text-caption text-ink-low">{item.label}</span>
            <span className="font-mono text-xl font-medium tabular-nums text-ink">
              {item.value}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function DirectoryFilters({
  units,
  search,
  unitFilter,
  onSearch,
  onUnitFilter,
}: {
  readonly units: readonly OrgUnitSummary[];
  readonly search: string;
  readonly unitFilter: UnitFilter;
  readonly onSearch: (value: string) => void;
  readonly onUnitFilter: (value: UnitFilter) => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="grid grid-cols-1 gap-3 rounded-lg border border-line bg-raised p-3 lg:grid-cols-[minmax(0,1fr)_16rem]">
      <label className="flex min-w-0 items-center gap-2 rounded-md border border-line bg-card px-3 py-2 focus-within:ring-2 focus-within:ring-forest-bright/40">
        <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-ink-low" />
        <input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={t('organization.filter.search_placeholder')}
          className="min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-ink-faint"
        />
      </label>
      <select
        value={unitFilter}
        onChange={(event) => onUnitFilter(event.target.value)}
        className="rounded-md border border-line bg-card px-3 py-2 text-body font-medium text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40"
      >
        <option value="all">{t('organization.filter.all_units')}</option>
        <option value="unassigned">{t('organization.filter.unassigned')}</option>
        {units.map((unit) => (
          <option key={unit.id} value={unit.id}>
            {unit.path}
          </option>
        ))}
      </select>
    </div>
  );
}

function MemberRoleCell({
  member,
  roles,
  currentAccountId,
  canManageMembers,
  rolePending,
  roleError,
  onRoleAssignment,
}: {
  readonly member: OrgMemberSummary;
  readonly roles: readonly OrgRoleView[];
  readonly currentAccountId: string | undefined;
  readonly canManageMembers: boolean;
  readonly rolePending: boolean;
  readonly roleError: { readonly accountId: string; readonly message: string } | null;
  readonly onRoleAssignment: (member: OrgMemberSummary, nextRole: string) => void;
}): JSX.Element {
  const t = useT();
  const assignedId = memberAssignedRoleId(member);
  const isSelf = currentAccountId === member.accountId;
  const isOwner = member.role === 'owner';
  if (!canManageMembers || isOwner) {
    const label = isBuiltinOrgRoleId(assignedId)
      ? t(builtinRoleLabelKey(assignedId))
      : (roles.find((role) => role.id === assignedId)?.name ?? t(ROLE_LABEL_KEY[member.role]));
    return <span data-testid={`organization-member-role-${member.accountId}`}>{label}</span>;
  }
  const options = assignableOrgRoles(roles);
  return (
    <span className="flex min-w-0 flex-col gap-1">
      <select
        value={assignedId}
        disabled={rolePending}
        onChange={(event) => onRoleAssignment(member, event.target.value)}
        title={t('organization.column.role')}
        data-testid={`organization-member-role-${member.accountId}`}
        className="min-w-0 w-full rounded-md border border-line bg-raised px-2 py-1 text-caption font-medium text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40 disabled:opacity-50"
      >
        {options.map((role) => {
          const lockout = isSelf && !roleHasMembersManage(role);
          const optionLabel = isBuiltinOrgRoleId(role.id)
            ? t(builtinRoleLabelKey(role.id))
            : role.name;
          return (
            <option key={role.id} value={role.id} disabled={lockout}>
              {lockout
                ? `${optionLabel} — ${t('organization.members.role.self_lockout')}`
                : optionLabel}
            </option>
          );
        })}
      </select>
      {isSelf ? (
        <p
          className="m-0 text-caption text-ink-low"
          data-testid={`organization-member-role-lockout-${member.accountId}`}
        >
          {t('organization.members.role.self_lockout')}
        </p>
      ) : null}
      {roleError !== null && roleError.accountId === member.accountId ? (
        <p
          className="m-0 text-caption text-bad"
          role="alert"
          data-testid={`organization-member-role-error-${member.accountId}`}
        >
          {roleError.message}
        </p>
      ) : null}
    </span>
  );
}

/**
 * Owner status writes are refused with 403. The API does accept a
 * member disabling their own account, which then locks that account
 * out of the organization, so neither control is offered.
 */
function canToggleMemberStatus(
  member: OrgMemberSummary,
  currentAccountId: string | undefined,
  canManageMembers: boolean,
): boolean {
  if (!canManageMembers || member.role === 'owner' || currentAccountId === member.accountId) {
    return false;
  }
  return member.status === 'active' || member.status === 'disabled';
}

function memberLabel(member: OrgMemberSummary): string {
  return member.displayName ?? member.email;
}

function MemberStatusCell({
  member,
  currentAccountId,
  canManageMembers,
  statusPending,
  statusPendingAccountId,
  statusError,
  hideError,
  onDisable,
  onEnable,
}: {
  readonly member: OrgMemberSummary;
  readonly currentAccountId: string | undefined;
  readonly canManageMembers: boolean;
  readonly statusPending: boolean;
  readonly statusPendingAccountId: string | undefined;
  readonly statusError: { readonly accountId: string; readonly message: string } | null;
  readonly hideError: boolean;
  readonly onDisable: (member: OrgMemberSummary) => void;
  readonly onEnable: (member: OrgMemberSummary) => void;
}): JSX.Element {
  const t = useT();
  const enabling = member.status === 'disabled';
  const canToggle = canToggleMemberStatus(member, currentAccountId, canManageMembers);
  const label = memberLabel(member);
  const rowError =
    !hideError && statusError !== null && statusError.accountId === member.accountId
      ? statusError
      : null;
  return (
    <span className="flex min-w-0 flex-col items-start gap-1">
      {enabling ? (
        <Chip tone="bad" testId={`organization-member-status-${member.accountId}`}>
          {t('organization.members.status.disabled')}
        </Chip>
      ) : null}
      {canToggle ? (
        <Button
          variant={enabling ? 'primary' : 'danger'}
          size="sm"
          disabled={statusPending}
          loading={statusPendingAccountId === member.accountId}
          onClick={() => (enabling ? onEnable(member) : onDisable(member))}
          aria-label={t(
            enabling
              ? 'organization.members.status.enable_aria'
              : 'organization.members.status.disable_aria',
            { member: label },
          )}
          data-testid={
            enabling
              ? `organization-member-enable-${member.accountId}`
              : `organization-member-disable-${member.accountId}`
          }
        >
          {t(
            enabling ? 'organization.members.status.enable' : 'organization.members.status.disable',
          )}
        </Button>
      ) : null}
      {rowError !== null ? (
        <p
          className="m-0 text-caption text-bad"
          role="alert"
          data-testid={`organization-member-status-error-${member.accountId}`}
        >
          {rowError.message}
        </p>
      ) : null}
    </span>
  );
}

function memberLimitLabel(
  usdCents: number | null,
  cnyPerUsd: number | null | undefined,
  t: ReturnType<typeof useT>,
): string {
  if (usdCents === null) {
    return t('organization.access.unrestricted');
  }
  // Rate still loading or unavailable: keep the old "capped" word so the
  // row does not flash an em dash over a ceiling that does exist.
  if (!isValidDisplayRate(cnyPerUsd)) {
    return t('organization.access.limited');
  }
  return t('organization.access.amount', {
    amount: formatUsdCentsAsCny(usdCents, cnyPerUsd),
  });
}

function MemberDirectory({
  members,
  units,
  roles,
  currentAccountId,
  canManageMembers,
  canReadOrgKeys,
  keyCounts,
  keysLoading,
  assignmentPending,
  rolePending,
  statusPending,
  statusPendingAccountId,
  assignError,
  roleError,
  statusError,
  dialogErrorAccountId,
  onAssignment,
  onRoleAssignment,
  onDisable,
  onEnable,
  onSelectUnit,
  onManageKeys,
  onEditLimit,
}: {
  readonly members: readonly OrgMemberSummary[];
  readonly units: readonly OrgUnitSummary[];
  readonly roles: readonly OrgRoleView[];
  readonly currentAccountId: string | undefined;
  readonly canManageMembers: boolean;
  readonly canReadOrgKeys: boolean;
  readonly keyCounts: ReadonlyMap<string, number>;
  readonly keysLoading: boolean;
  readonly assignmentPending: boolean;
  readonly rolePending: boolean;
  readonly statusPending: boolean;
  readonly statusPendingAccountId: string | undefined;
  readonly assignError: { readonly accountId: string; readonly message: string } | null;
  readonly roleError: { readonly accountId: string; readonly message: string } | null;
  readonly statusError: { readonly accountId: string; readonly message: string } | null;
  readonly dialogErrorAccountId: string | null;
  readonly onAssignment: (member: OrgMemberSummary, nextOrgUnitId: string) => void;
  readonly onRoleAssignment: (member: OrgMemberSummary, nextRole: string) => void;
  readonly onDisable: (member: OrgMemberSummary) => void;
  readonly onEnable: (member: OrgMemberSummary) => void;
  readonly onSelectUnit: (id: string | null) => void;
  readonly onManageKeys: (member: OrgMemberSummary) => void;
  readonly onEditLimit: (member: OrgMemberSummary) => void;
}): JSX.Element {
  const t = useT();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  if (members.length === 0) {
    return (
      <p className="rounded-lg border border-line bg-card p-4 text-body text-ink-low">
        {t('organization.members.empty')}
      </p>
    );
  }
  return (
    // Eight columns need a floor or they crush into each other, and the
    // header shares the scroll box with the rows so sideways scrolling
    // cannot slide the labels off their columns. The floor is `md`-only:
    // below `md` the Field Notes table (DESIGN.md §6) turns each row into
    // a card, and an unconditional minimum would suppress that and hand a
    // phone a 1280px sideways scroll instead — which is exactly what the
    // hand-rolled grid this replaced used to do.
    <Table testId="organization-members-scroll" className="md:[&>table]:min-w-[80rem]">
      <THead data-testid="organization-members-header">
        <Tr interactive={false}>
          <Th>{t('organization.column.member')}</Th>
          <Th className="w-[10rem]">{t('organization.column.status')}</Th>
          <Th className="w-[11rem]">{t('organization.column.unit')}</Th>
          <Th className="w-[6rem]">{t('organization.column.role')}</Th>
          <Th className="w-[14rem]">{t('organization.column.access')}</Th>
          <Th className="w-[8rem]">{t('organization.column.permissions')}</Th>
          <Th className="w-[6rem]">{t('organization.column.keys')}</Th>
          <Th className="w-[8rem]">{t('organization.column.joined')}</Th>
        </Tr>
      </THead>
      <TBody>
        {members.map((member) => (
          <Tr
            key={member.accountId}
            interactive={false}
            data-testid={`organization-member-row-${member.accountId}`}
          >
            <Td heading>
              <span className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-forest font-medium text-on-forest">
                  {avatarInitial(member)}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-ink">
                    {member.displayName ?? member.email}
                  </span>
                  <span className="truncate font-mono text-caption text-ink-low">
                    {member.email}
                  </span>
                </span>
              </span>
            </Td>
            <Td stack label={t('organization.column.status')}>
              <MemberStatusCell
                member={member}
                currentAccountId={currentAccountId}
                canManageMembers={canManageMembers}
                statusPending={statusPending}
                statusPendingAccountId={statusPendingAccountId}
                statusError={statusError}
                hideError={dialogErrorAccountId === member.accountId}
                onDisable={onDisable}
                onEnable={onEnable}
              />
            </Td>
            <Td stack label={t('organization.column.unit')}>
              {canManageMembers ? (
                <select
                  value={member.orgUnitId ?? ''}
                  disabled={assignmentPending}
                  onChange={(event) => onAssignment(member, event.target.value)}
                  title={t('organization.column.unit')}
                  data-testid={`organization-member-unit-${member.accountId}`}
                  className="min-w-0 w-full rounded-md border border-line bg-raised px-2 py-1 text-caption font-medium text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40 disabled:opacity-50"
                >
                  <option value="">{t('organization.filter.unassigned')}</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.path}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  type="button"
                  onClick={() => onSelectUnit(member.orgUnitId)}
                  className="min-w-0 w-full truncate rounded-md bg-raised px-2 py-1 text-left font-mono text-caption text-ink-mid hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                >
                  {member.orgUnitPath ?? member.department ?? t('organization.filter.unassigned')}
                </button>
              )}
              {assignError !== null && assignError.accountId === member.accountId ? (
                <p
                  className="m-0 text-caption text-bad"
                  role="alert"
                  data-testid={`organization-member-unit-error-${member.accountId}`}
                >
                  {assignError.message}
                </p>
              ) : null}
            </Td>
            <Td label={t('organization.column.role')}>
              <MemberRoleCell
                member={member}
                roles={roles}
                currentAccountId={currentAccountId}
                canManageMembers={canManageMembers}
                rolePending={rolePending}
                roleError={roleError}
                onRoleAssignment={onRoleAssignment}
              />
            </Td>
            <Td label={t('organization.column.access')}>
              <span className="flex min-w-0 flex-wrap items-center gap-1">
                <span
                  data-testid={`organization-member-limit-${member.accountId}`}
                  className={
                    member.monthlyLimitUsdCents === null
                      ? 'inline-flex w-fit rounded-md bg-raised px-2 py-1 text-caption font-medium text-ink'
                      : 'inline-flex w-fit rounded-md bg-raised px-2 py-1 font-mono text-caption font-medium tabular-nums text-ink'
                  }
                >
                  {memberLimitLabel(member.monthlyLimitUsdCents, cnyPerUsd, t)}
                </span>
                {/*
                  Owner and the signed-in account stay editable. platform-api
                  refuses owner status and role changes on this PATCH, not a
                  monthly ceiling — hiding the control would block a write
                  the server accepts.
                */}
                {canManageMembers ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditLimit(member)}
                    aria-label={t('organization.members.limit.edit_aria', {
                      member: member.displayName ?? member.email,
                    })}
                    data-testid={`organization-member-limit-edit-${member.accountId}`}
                  >
                    {t('organization.members.limit.set')}
                  </Button>
                ) : null}
              </span>
            </Td>
            <Td label={t('organization.column.permissions')}>
              <span className="flex min-w-0">
                <Chip
                  tone={modelPermissionModeTone(member.modelPermissionMode)}
                  lamp={false}
                  testId={`organization-member-permission-${member.accountId}`}
                >
                  {t(modelPermissionModeLabelKey(member.modelPermissionMode))}
                </Chip>
              </span>
            </Td>
            <Td label={t('organization.column.keys')}>
              {!canReadOrgKeys ? (
                <span className="font-mono text-caption text-ink-faint">--</span>
              ) : member.role === 'owner' ? (
                // An owner's keys are not scoped to them the way a
                // member's are, so a count here would be misleading.
                <span className="text-caption text-ink-low">
                  {t('organization.members.keys.owner_note')}
                </span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={keysLoading}
                  onClick={() => onManageKeys(member)}
                  aria-label={t('organization.members.keys.manage', {
                    member: member.displayName ?? member.email,
                  })}
                  data-testid={`organization-member-keys-${member.accountId}`}
                >
                  <span className="font-mono">
                    {keysLoading
                      ? t('organization.members.keys.loading')
                      : String(keyCounts.get(member.accountId) ?? 0)}
                  </span>
                </Button>
              )}
            </Td>
            <Td mono label={t('organization.column.joined')} className="text-caption text-ink-low">
              {formatDate(member.joinedAt)}
            </Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

function OrgUnitDetail({
  unit,
  units,
}: {
  readonly unit: OrgUnitSummary | null;
  readonly units: readonly OrgUnitSummary[];
}): JSX.Element {
  const t = useT();
  const parent =
    unit?.parentId === null
      ? null
      : (units.find((candidate) => candidate.id === unit?.parentId) ?? null);
  return (
    <aside
      data-testid="organization-unit-detail"
      className="flex min-h-[18rem] flex-col gap-4 rounded-lg border border-line bg-card p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-body font-medium text-ink">{t('organization.detail.title')}</h3>
        <Network aria-hidden="true" className="h-5 w-5 text-forest" />
      </div>
      {unit === null ? (
        <p className="text-body leading-6 text-ink-low">{t('organization.detail.empty')}</p>
      ) : (
        <>
          <div>
            <p className="text-lg font-medium text-ink">{unit.name}</p>
            <p className="mt-1 font-mono text-caption text-ink-low">{unit.path}</p>
          </div>
          <dl className="grid grid-cols-1 gap-3 text-body">
            <DetailRow label={t('organization.detail.parent')} value={parent?.path ?? '-'} />
            <DetailRow
              label={t('organization.detail.direct_members')}
              value={String(unit.memberCount)}
            />
            <DetailRow label={t('organization.detail.status')} value={unit.status} />
          </dl>
        </>
      )}
    </aside>
  );
}

function DetailRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-raised px-3 py-2">
      <dt className="text-ink-low">{label}</dt>
      <dd className="min-w-0 truncate font-mono text-ink">{value}</dd>
    </div>
  );
}

function GovernancePanel({
  assignedMembers,
  limitedMembers,
  managerMembers,
}: {
  readonly assignedMembers: number;
  readonly limitedMembers: number;
  readonly managerMembers: number;
}): JSX.Element {
  const t = useT();
  return (
    <div className="rounded-lg border border-line bg-card p-4">
      <PanelHeading
        title={t('organization.governance.title')}
        subtitle={t('organization.governance.subtitle')}
      />
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricTile
          label={t('organization.governance.assigned')}
          value={assignedMembers}
          icon={ClipboardCheck}
        />
        <MetricTile
          label={t('organization.governance.limited')}
          value={limitedMembers}
          icon={Lock}
        />
        <MetricTile
          label={t('organization.governance.managers')}
          value={managerMembers}
          icon={ShieldCheck}
        />
      </div>
    </div>
  );
}

function MetricTile({
  label,
  value,
  icon,
}: {
  readonly label: string;
  readonly value: number;
  readonly icon: LucideIcon;
}): JSX.Element {
  const IconGlyph = icon;
  return (
    <div className="flex items-center gap-3 rounded-lg bg-raised px-4 py-3">
      <IconGlyph aria-hidden="true" className="h-5 w-5 shrink-0 text-forest" />
      <div>
        <p className="text-caption text-ink-low">{label}</p>
        <p className="font-mono text-xl font-medium text-ink">{value}</p>
      </div>
    </div>
  );
}

function PanelHeading({
  title,
  subtitle,
}: {
  readonly title: string;
  readonly subtitle: string;
}): JSX.Element {
  return (
    <header className="flex flex-col gap-1">
      <h3 className="text-body font-medium text-ink">{title}</h3>
      {subtitle.length > 0 ? <p className="text-caption text-ink-low">{subtitle}</p> : null}
    </header>
  );
}

function LoadingState(): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2" data-testid="organization-loading">
      {[0, 1, 2].map((idx) => (
        <Skeleton key={idx} className="h-14" />
      ))}
      <span className="sr-only">{t('organization.loading')}</span>
    </div>
  );
}

/**
 * A failed load reports which list failed and offers a retry — the three
 * queries are independent, so one failure must neither hide the others
 * nor force a full page reload to recover.
 */
function QueryError({
  error,
  fallback,
  testId,
  onRetry,
}: {
  readonly error: unknown;
  readonly fallback: string;
  readonly testId: string;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="rounded-lg border border-bad-line bg-card p-3">
      <PanelError
        message={formatOrgApiError(error, fallback, t)}
        retryLabel={t('organization.retry')}
        onRetry={onRetry}
        testId={testId}
      />
    </div>
  );
}

function formatOrgApiError(error: unknown, fallback: string, t: ReturnType<typeof useT>): string {
  return error instanceof PlatformApiError ? localizeApiError(error, t) : fallback;
}

function pickDefaultUnitId(units: readonly OrgUnitSummary[]): string | null {
  if (units.length === 0) return null;
  const sorted = units.slice().sort((a, b) => {
    const topLevel = Number(b.parentId === null) - Number(a.parentId === null);
    if (topLevel !== 0) return topLevel;
    if (b.memberCount !== a.memberCount) return b.memberCount - a.memberCount;
    return a.path.localeCompare(b.path);
  });
  return sorted[0]?.id ?? null;
}

function filterMembers(
  members: readonly OrgMemberSummary[],
  search: string,
  unitFilter: UnitFilter,
): OrgMemberSummary[] {
  const needle = search.trim().toLowerCase();
  return members.filter((member) => {
    if (unitFilter === 'unassigned' && member.orgUnitId !== null) return false;
    if (unitFilter !== 'all' && unitFilter !== 'unassigned' && member.orgUnitId !== unitFilter)
      return false;
    if (needle.length === 0) return true;
    const haystack = [
      member.displayName ?? '',
      member.email,
      member.department ?? '',
      member.orgUnitName ?? '',
      member.orgUnitPath ?? '',
      member.role,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
}

function avatarInitial(member: OrgMemberSummary): string {
  const source = member.displayName ?? member.email;
  return source.trim().slice(0, 1).toUpperCase();
}

/**
 * Keys count towards a member only when they are actually assigned to
 * one: an unassigned key belongs to the organization at large, and a key
 * assigned to an account that is not a listed member (or to an owner,
 * whose keys are org-wide) says nothing about this directory.
 */
function countMemberApiKeys(
  keys: readonly ApiKeySummary[],
  members: readonly OrgMemberSummary[],
): ReadonlyMap<string, number> {
  // The owner filter is redundant but deliberate: the table never asks
  // for an owner's count, so no test can reach it. It stays because the
  // rule belongs to the counting, not to the one caller that respects it.
  const memberAccounts = new Set(
    members.filter((member) => member.role !== 'owner').map((member) => member.accountId),
  );
  const counts = new Map<string, number>();
  for (const key of keys) {
    if (key.assignedAccountId === null || !memberAccounts.has(key.assignedAccountId)) {
      continue;
    }
    counts.set(key.assignedAccountId, (counts.get(key.assignedAccountId) ?? 0) + 1);
  }
  return counts;
}

function keysForMember(
  keys: readonly ApiKeySummary[],
  member: OrgMemberSummary,
): readonly ApiKeySummary[] {
  // Redundant but deliberate, same as above: the keys dialog is not
  // offered for an owner, so this early return is unreachable from the
  // UI. Do not delete it as dead code.
  if (member.role === 'owner') return [];
  return keys.filter((key) => key.assignedAccountId === member.accountId);
}

const TAB_ICON: Readonly<Record<TabId, LucideIcon>> = {
  members: Users,
  structure: Network,
  permissions: SlidersHorizontal,
  model_permissions: Lock,
  invitations: MailOpen,
  governance: ShieldCheck,
  audit: ScrollText,
};

function TabIconGlyph({ tab }: { readonly tab: TabId }): JSX.Element {
  const IconGlyph = TAB_ICON[tab];
  return <IconGlyph aria-hidden="true" className="h-4 w-4" />;
}

function isTabId(value: string | null): value is TabId {
  return value !== null && TAB_IDS.some((tab) => tab === value);
}
